// src/hooks/useIdleLogout.js
import { useEffect, useRef } from "react";

const CHANNEL = "gamerin-session";
const LAST_ACTIVITY_KEY = "lastActivityAt";
const LOGOUT_FLAG_KEY = "forceLogout";
const INACTIVITY_LIMIT_MS = 60 * 60 * 1000; // 1 hora
const CHECK_EVERY_MS = 30 * 1000; // comprueba cada 30s

export default function useIdleLogout(
  onLogout,
  {
    limitMs = INACTIVITY_LIMIT_MS,
    checkEveryMs = CHECK_EVERY_MS,
    warnAtMs = null, // ej. 55 * 60 * 1000 para avisar 5 min antes (opcional)
    onWarn = null, // callback aviso (opcional)
  } = {}
) {
  const bcRef = useRef(null);
  const warnedRef = useRef(false);

  // Marca de actividad (local y compartida)
  const stampActivity = () => {
    try {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
      bcRef.current?.postMessage({ type: "activity", ts: Date.now() });
    } catch {}
  };

  // Handlers de actividad del usuario + volver al tab (con CHECK real)
  useEffect(() => {
    if (typeof onLogout !== "function") return;

    const activityEvents = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    const onAnyActivity = () => stampActivity();

    const checkNow = () => {
      const last = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || 0);
      const idleFor = Date.now() - last;

      // Si ya pasó el límite, logout inmediato al recuperar foco
      if (idleFor >= limitMs) {
        try {
          localStorage.setItem(LOGOUT_FLAG_KEY, "1");
          bcRef.current?.postMessage({ type: "logout" });
        } catch {}
        onLogout?.();
        return;
      }

      // Si NO toca logout, entonces sí: marcamos actividad al volver
      stampActivity();
    };

    const onVis = () => {
      if (!document.hidden) checkNow();
    };

    activityEvents.forEach((e) => window.addEventListener(e, onAnyActivity, { passive: true }));
    document.addEventListener("visibilitychange", onVis);

    // Inicial
    if (!localStorage.getItem(LAST_ACTIVITY_KEY)) stampActivity();

    return () => {
      activityEvents.forEach((e) => window.removeEventListener(e, onAnyActivity));
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [limitMs, onLogout]);

  // BroadcastChannel para sincronizar entre pestañas
  useEffect(() => {
    if (typeof onLogout !== "function") return;

    try {
      const bc = new BroadcastChannel(CHANNEL);
      bcRef.current = bc;
      bc.onmessage = (ev) => {
        if (ev?.data?.type === "logout") onLogout?.();
        if (ev?.data?.type === "activity") {
          warnedRef.current = false;
        }
      };
      return () => bc.close();
    } catch {
      // Safari viejo/privado: sin BroadcastChannel, nos vale el storage event
    }
  }, [onLogout]);

  // Escucha de storage (fallback ↔ sync entre pestañas)
  useEffect(() => {
    if (typeof onLogout !== "function") return;

    const onStorage = (e) => {
      if (e.key === LOGOUT_FLAG_KEY && e.newValue === "1") onLogout?.();
      if (e.key === LAST_ACTIVITY_KEY) warnedRef.current = false;
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [onLogout]);

  // Bucle de comprobación / aviso / logout
  useEffect(() => {
    if (typeof onLogout !== "function") return;

    const check = () => {
      const last = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || 0);
      const idleFor = Date.now() - last;

      // Aviso previo (opcional)
      if (warnAtMs && onWarn && idleFor >= warnAtMs && idleFor < limitMs && !warnedRef.current) {
        warnedRef.current = true;
        onWarn(idleFor, limitMs);
      }

      // Logout por inactividad
      if (idleFor >= limitMs) {
        try {
          localStorage.setItem(LOGOUT_FLAG_KEY, "1");
          bcRef.current?.postMessage({ type: "logout" });
        } catch {}
        onLogout?.();
      }
    };

    const id = setInterval(check, checkEveryMs);
    check();
    return () => clearInterval(id);
  }, [limitMs, checkEveryMs, warnAtMs, onWarn, onLogout]);

  // API pública del hook
  return { touch: () => stampActivity() };
}
